/**
 * API client for entitlement endpoints.
 * Provides quota state, capability state, and add-on state queries.
 *
 * @userstory US-ENT-01, US-ENT-02, US-ENT-03
 */
import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/** Quota state for a single quota key. */
export interface QuotaState {
	quotaKey: string;
	used: number;
	/** null = unlimited */
	limit: number | null;
	/** int.MaxValue when unlimited */
	remaining: number;
	resetAt: string; // ISO-8601 UTC
}

export interface CapabilityState {
	capabilityKey: string;
	included: boolean;
}

export interface AddonState {
	addonKey: string;
	active: boolean;
	everActive?: boolean;
}

// ============================================================================
// API functions
// ============================================================================

/**
 * Fetches quota state for a given quota key.
 * Does NOT consume the quota.
 *
 * @userstory US-ENT-02
 */
export async function getQuotaState(organizationId: string, quotaKey: string): Promise<QuotaState> {
	const response = await apiClient.get<QuotaState>(
		`/api/organizations/${organizationId}/entitlements/quota/${quotaKey}`,
	);
	return response.data;
}

/**
 * Fetches capability state for a given capability key.
 *
 * @userstory US-ENT-01
 */
export async function getCapabilityState(organizationId: string, capabilityKey: string): Promise<CapabilityState> {
	const response = await apiClient.get<CapabilityState>(
		`/api/organizations/${organizationId}/entitlements/capability/${capabilityKey}`,
	);
	return response.data;
}

/**
 * Fetches add-on state for a given add-on key.
 *
 * @userstory US-ENT-03
 */
export async function getAddonState(organizationId: string, addonKey: string): Promise<AddonState> {
	const response = await apiClient.get<AddonState>(
		`/api/organizations/${organizationId}/entitlements/addon/${addonKey}`,
	);
	return response.data;
}

const entitlementService = { getQuotaState, getCapabilityState, getAddonState };
export default entitlementService;
